import { IFaceted, IFacetMapping, IFacet, pack } from '../Facet';
import { getItemsOperation } from '../../operations/getItemsOperation';
import { IItemCacheState, getCacheItem } from '../../store/reducers/itemCacheReducer';
import { IItemCache } from '../IItemCacheObserver';
import { DemandResolution, resolvedFacet } from '../../facets/DemandFacets';
import { mapObject, areShallowEqual } from '../../utilities/objects';
import { preferredKeyFacetFacet, operationQueueHandlerFacet } from '../../facets/OperationFacets';
import { KeyFacet } from '../KeyFacet';
import { ComputedItemFacet } from './ComputedItemFacet';
import { IControlFaceted } from '../../components/decorators/withControlHandler';
import { ControlFacet, controlTypeKeyFacet } from '../controls/ControlFacet';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { getOperationTypeKey } from '../../store/reducers/operationMapReducer';
import { is } from '@ms/utilities-objects/lib/is';

const ERROR_ITEM_CACHE_DISPOSED =
  'The IItemCache instance may only be used within an invocation of `mapItemCacheToProps` ' +
  'passed to`connectToItemCache`.';

export interface IItemCacheDependencies {
  [itemKey: string]: IFaceted;
}

export interface IItemCacheDemands {
  [itemKey: string]: typeof getItemsOperation.type;
}

export interface IItemCacheParams {
  state: IItemCacheState;
  previousDependencies?: IItemCacheDependencies;
  previousDemands?: IItemCacheDemands;
  controlHandlers?: IControlFaceted;
  dispatch: IItemCacheDispatch;
}

/**
* Implementation of the component which provides access to the item cache,
* while also recording what is demanded along with the state of all cache
* entries at the time they are demanded.
*/
export class ItemCacheObserver implements IItemCache {
  public readonly dispatch: IItemCacheDispatch;

  private _memoizedGetCacheItem: (itemKey: string) => IFaceted | undefined;
  private _previousDependencies: IItemCacheDependencies | undefined;
  private _previousDemands: IItemCacheDemands | undefined;
  private _controlHandlers?: IControlFaceted;

  private _isDisposed: boolean | undefined;

  private _items: {
    [itemKey: string]: IFaceted;
  };

  private _demands: {
    [itemKey: string]: IFaceted<boolean>;
  };

  private _facets: {
    [itemKey: string]: IFaceted;
  };

  private _computations: {
    [itemKey: string]: IFaceted;
  };

  private _resolutions: {
    [itemKey: string]: IFaceted<DemandResolution>;
  };

  constructor(params: IItemCacheParams) {
    this.dispatch = params.dispatch;

    this._memoizedGetCacheItem = memoizeGetCacheItem(params.state);
    this._previousDemands = params.previousDemands;
    this._previousDependencies = params.previousDependencies;
    this._controlHandlers = params.controlHandlers;

    this._items = {};
    this._facets = {};
    this._computations = {};
    this._demands = {};
    this._resolutions = {};
  }

  public demandItem<TFacets extends object>(
    mapping: IFacetMapping<TFacets>,
    itemKey: string | undefined): Partial<TFacets> {

    if (this._isDisposed) {
      throw new Error(ERROR_ITEM_CACHE_DISPOSED);
    }

    const facets = {} as Partial<TFacets>;

    for (const name in mapping) {
      if (Object.prototype.hasOwnProperty.call(mapping, name)) {
        const facet = mapping[name];

        if (!facet) {
          throw new Error(
            `Mapping '${name}' was not passed a valid Facet or IFacet instance in demandItem.`
          );
        }

        facets[name] = this.demandItemFacet(facet, itemKey);
      }
    }

    return facets;
  }

  public demandItemFacet<TValue>(facet: IFacet<TValue>, itemKey: string | undefined): TValue | undefined {
    if (this._isDisposed) {
      throw new Error(ERROR_ITEM_CACHE_DISPOSED);
    }

    if (facet instanceof ControlFacet && !controlTypeKeyFacet.matches(itemKey)) {
      return this._addControl(itemKey, facet);
    }

    if (facet instanceof ComputedItemFacet) {
      return this._addComputation(itemKey, facet);
    }

    if (!itemKey) {
      return undefined;
    }

    const item = this._addItem(itemKey);

    if (facet.id !== resolvedFacet.id) {
      // The resolved facet cannot itself be resolved.
      this._addResolution(itemKey, item, facet);
    }

    const value = this._addValue(itemKey, item, facet);

    if (!value && (facet instanceof KeyFacet)) {
      // If the facet is a KeyFacet, then do the caller a favor and parse the input key if possible.
      // This can assist in making a decision while waiting for the real item to resolve.
      if (facet.matches(itemKey)) {
        return facet.deserialize(itemKey);
      }
    }

    return value;
  }

  public isItemFacetResolved<TValue>(facet: IFacet<TValue>, itemKey: string | undefined): boolean {
    if (this._isDisposed) {
      throw new Error(ERROR_ITEM_CACHE_DISPOSED);
    }

    if (!itemKey) {
      return false;
    }

    if (facet instanceof ComputedItemFacet) {
      return false;
    }

    const item = this._addItem(itemKey);

    const demandResolution = this._addResolution(itemKey, item, facet);

    return demandResolution === DemandResolution.resolved;
  }

  public haveDependenciesChanged(): boolean {
    const previousDependencies = this._previousDependencies;

    if (!previousDependencies) {
      return true;
    }

    // This function can't use a general deep-comparer because facets should not be deep-compared, with
    // exception of resolvedFacet.
    for (const itemKey in previousDependencies) {
      if (Object.prototype.hasOwnProperty.call(previousDependencies, itemKey)) {
        const previousItem = previousDependencies[itemKey];

        // Memoize accesses from the cache for performance, since it reduces key-mapping work.
        const item = this._memoizedGetCacheItem(itemKey);

        for (const facetId in previousItem) {
          if (Object.prototype.hasOwnProperty.call(previousItem, facetId)) {
            if (facetId === resolvedFacet.id) {
              const previousResolved = resolvedFacet.unpack(previousItem);
              const resolved = item && resolvedFacet.unpack(item);

              const previousDemands = previousResolved && previousResolved.demands;
              const demands = resolved && resolved.demands;

              if (previousDemands) {
                for (const resolvedFacetId in previousDemands) {
                  if (Object.prototype.hasOwnProperty.call(previousDemands, resolvedFacetId)) {
                    const previousResolution = previousDemands[resolvedFacetId];
                    const resolution = demands && demands[resolvedFacetId];

                    if (normalizeDemandResolution(previousResolution) !== normalizeDemandResolution(resolution)) {
                      return {
                        [itemKey]: {
                          [facetId]: {
                            demands: {
                              [resolvedFacetId]: {
                                previous: previousResolution,
                                current: resolution
                              }
                            }
                          }
                        }
                      } as {} as boolean;
                    }
                  }
                }
              }
            } else {
              const previousFacet = previousItem[facetId];
              const facet = item && item[facetId];

              if (!is(previousFacet, facet)) {
                return {
                  [itemKey]: {
                    [facetId]: {
                      previous: previousFacet,
                      current: facet
                    }
                  }
                } as {} as boolean;
              }
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * Marks this item cache observer as disposed so callers can't access it asynchronously.
   */
  public dispose(): void {
    this._isDisposed = true;
  }

  /**
   * Gets the current snapshot of demands from this item cache observer,
   * if they have differed from previous demands.
   *
   * The goal here is to track which facets the caller has demanded, and
   * emit the payload for the getItems request.
   */
  public getDemands(): IItemCacheDemands {
    const demands = mapObject(
      this._demands,
      (facets: IFaceted, itemKey: string): typeof getItemsOperation.type => {
        return {
          demands: {
            ...pack({}, {}),
            ...mapObject(facets, (value: {}, facetId: string) => {
              return true;
            })
          }
        };
      }
    );

    const isDirty = !areShallowEqual(demands, this._previousDemands, (left: {
      demands: IFaceted<boolean>
    }, right: {
      demands: IFaceted<boolean>;
    }) => areShallowEqual(left.demands, right.demands));

    return isDirty || !this._previousDemands ? demands : this._previousDemands;
  }

  /**
   * Gets the current snapshot of dependencies from this item cache observer,
   * if they have differed from the previous state.
   *
   * The goal here is to to produce a mapping of all referenced
   * item keys to the current values of demanded facets,
   * along with the current resolution states of each referenced facet.
   * At the same time, compare the result against the previous dependencies
   * to determine if the previous dependencies may be reused.
   */
  public getDependencies(): IItemCacheDependencies {
    const dependencies = mapObject(
      this._items,
      (item: IFaceted, itemKey: string): IFaceted => {
        const facets = this._facets[itemKey] || pack({}, {});
        const resolutions = this._resolutions[itemKey] || pack({}, {});

        return {
          ...facets,
          ...resolvedFacet.pack({
            demands: resolutions
          })
        };
      }
    );

    const isDirty = !areShallowEqual(
      dependencies,
      this._previousDependencies,
      (left: {}, right: {}, itemKey: string) => {
        const {
          [resolvedFacet.id]: {
            demands: leftDemands
          },
          ...leftFacets
        } = left as IFaceted;

        const {
          [resolvedFacet.id]: {
            demands: rightDemands
          },
          ...rightFacets
        } = right as IFaceted;

        return areShallowEqual(leftDemands, rightDemands) && areShallowEqual(leftFacets, rightFacets);
      });

    return isDirty || !this._previousDependencies ? dependencies : this._previousDependencies;
  }

  /**
   * Adds a dependency on an item.
   */
  private _addItem(itemKey: string): IFaceted | undefined {
    const item = this._memoizedGetCacheItem(itemKey);

    this._items[itemKey] = item!;

    return item;
  }

  /**
   * Adds a dependency on a computed facet.
   */
  private _addComputation<TValue>(itemKey: string | undefined, facet: ComputedItemFacet<TValue>): TValue | undefined {
    const computations = this._computations[itemKey || ''] || (this._computations[itemKey || ''] = pack({}, {}));

    const facetId = facet.id;

    if (facetId in computations) {
      // This computation has already been seen and its dependencies recorded.
      return computations[facetId] as TValue;
    }

    // For the moment, add the computation's dependencies to this observer's dependencies.
    // In the future, the goal is to maximize re-use of computations across observer instances.
    const value = facet.compute(this, { itemKey: itemKey || undefined });

    computations[facetId] = value!;

    return value;
  }

  private _addControl<TValue>(
    itemKey: string | undefined,
    facet: ControlFacet<{}, {}>): TValue | undefined {
    const item = itemKey && this._addItem(itemKey) || undefined;

    const keyFacet = itemKey && this._addPreferredKeyFacet(itemKey, item);

    const controlTypeKey = keyFacet && ControlFacet.getControlTypeKey({
      keyFacet: keyFacet,
      controlTypeFacet: facet
    });

    const controlRegistryItem = controlTypeKey && this._addItem(controlTypeKey) || undefined;

    const controlHandler = controlTypeKey && this._addValue(controlTypeKey, controlRegistryItem, facet) || undefined;

    const propsControlHandler = this._controlHandlers && facet.unpack(this._controlHandlers);

    return facet.compose(controlHandler, propsControlHandler) as {} as TValue | undefined;
  }

  /**
   * Adds a dependency and demand on a facet value.
   */
  private _addValue<TValue>(itemKey: string, item: IFaceted | undefined, facet: IFacet<TValue>): TValue | undefined {
    const facets = this._facets[itemKey] || (this._facets[itemKey] = pack({}, {}));
    const demands = this._demands[itemKey] || (this._demands[itemKey] = pack({}, {}));

    const facetId = facet.id;

    const value = item && item[facetId] as TValue;

    facets[facetId] = value!;

    if (facetId !== preferredKeyFacetFacet.id && facetId !== resolvedFacet.id) {
      demands[facetId] = true;
    }

    // Also add a dependency on the getItems operation handler.
    this._addOperationHandler(itemKey, item);

    return value;
  }

  private _addPreferredKeyFacet(itemKey: string, item: IFaceted | undefined): KeyFacet<{}> {
    const facets = this._facets[itemKey] || (this._facets[itemKey] = pack({}, {}));

    const preferredKeyFacet = item && item[preferredKeyFacetFacet.id] as KeyFacet<{}>;

    facets[preferredKeyFacetFacet.id] = preferredKeyFacet!;

    const preferredKey = preferredKeyFacet && item && item[preferredKeyFacet.id];

    if (preferredKeyFacet) {
      facets[preferredKeyFacet.id] = preferredKey!;
    }

    return preferredKeyFacet || KeyFacet.get(itemKey);
  }

  private _addOperationHandler(itemKey: string, item: IFaceted | undefined): void {
    // Add a dependency on the preferred KeyFacet and key for the item.
    const keyFacet = this._addPreferredKeyFacet(itemKey, item);

    const operationTypeKey = getOperationTypeKey({ operationTypeFacet: getItemsOperation, keyFacet });

    const operationFacets = this._facets[operationTypeKey] || (this._facets[operationTypeKey] = pack({}, {}));

    const operation = this._addItem(operationTypeKey);

    const operationHandler = operation && operation[operationQueueHandlerFacet.id];

    operationFacets[operationQueueHandlerFacet.id] = operationHandler!;
  }

  /**
   * Adds a dependency on a facet resolution state.
   */
  private _addResolution(itemKey: string, item: IFaceted | undefined, facet: IFacet<{}>): DemandResolution | undefined {
    const resolutions = this._resolutions[itemKey] || (this._resolutions[itemKey] = pack({}, {}));

    const resolved = item && resolvedFacet.unpack(item);
    const demands = resolved && resolved.demands;

    const facetId = facet.id;

    const resolution = demands && demands[facetId];

    resolutions[facetId] = normalizeDemandResolution(resolution);

    return resolution;
  }
}

const cacheMemoizer = new WeakMap<IItemCacheState, (itemKey: string) => (IFaceted | undefined)>();

/**
 * Create a version of getCacheItem memoized from a given state snapshot.
 * This avoid repeated key-mapping operations.
 */
function memoizeGetCacheItem(state: IItemCacheState): (itemKey: string) => IFaceted | undefined {
  if (cacheMemoizer.has(state)) {
    return cacheMemoizer.get(state)!;
  }

  const items: {
    [itemKey: string]: IFaceted;
  } = {};

  const memoizedGetCacheItem = (itemKey: string) => {
    return (itemKey in items) ?
      items[itemKey] :
      (items[itemKey] = getCacheItem(state, itemKey)!);
  };

  cacheMemoizer.set(state, memoizedGetCacheItem);

  return memoizedGetCacheItem;
}

/**
 * Since the only resolution states which matter for cache refreshes are 'resolved' and 'unresolved',
 * this function 'normalizes' a demand into only one of those two states.
 */
function normalizeDemandResolution(demandResolution?: DemandResolution): DemandResolution {
  return demandResolution === DemandResolution.resolved ? DemandResolution.resolved : DemandResolution.unresolved;
}
